import { compact, groupBy } from 'lodash';
import { useMemo } from 'react';

import { ButtonWithPopoverProps } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import {
  getAISuggestionsForEntitySection,
  getLabelForSection,
  getSectionsForEntityType,
} from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.utils';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import {
  EstatePage_EntityFragment,
  EstatePage_UnacknowledgedAiSuggestionFragment,
  EstatePage_UnacknowledgedHoldingSuggestionFragment,
} from '@/modules/estate/graphql/EstatePageData.generated';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';

import { isEstateEntitySuggestionReviewable } from './estateOnboarding.utils';
import { EstateOnboardingSuggestionsButton } from './EstateOnboardingSuggestionsButton';

export interface EstateOnboardingEntitySuggestionsButtonProps
  extends Partial<ButtonWithPopoverProps> {
  holdingsSuggestions: EstatePage_UnacknowledgedHoldingSuggestionFragment[];
  entity: EstatePage_EntityFragment;
  isGenerating?: boolean;
  /** Pre-computed V2 suggestions for this entity from the Estate context map. */
  entitySuggestionsV2?: EstatePage_UnacknowledgedAiSuggestionFragment[];
  onHoldingsSuggestionClick?: () => void;
  onEditEntitySectionSuggestionClick?: (section: EditEntitySection) => void;
}

export function EstateOnboardingEntitySuggestionsButton({
  holdingsSuggestions,
  entity,
  isGenerating = false,
  entitySuggestionsV2,
  onHoldingsSuggestionClick,
  onEditEntitySectionSuggestionClick,
  size = 'xs',
  variant = 'secondary',
  popperVariant = 'menuBelow',
  sx,
  ...props
}: EstateOnboardingEntitySuggestionsButtonProps) {
  const { defaultFirstToDieClientId } = useHouseholdDetailsContext();

  const entitySectionSuggestions = useMemo(() => {
    // Get entity type from entity kind for utility functions
    const entityType = getEntityTypeFromEntityKind(entity.kind);

    // Get the "edit entity sections" for the entity type.
    const entitySections = getSectionsForEntityType(entityType);

    const suggestions = (entitySuggestionsV2 ?? []).filter((suggestion) =>
      isEstateEntitySuggestionReviewable(
        suggestion,
        entity.kind,
        defaultFirstToDieClientId ?? undefined
      )
    );
    const suggestionsByKind = groupBy(suggestions, 'kind');

    // For each section, get the suggestions, filter out any accepted ones,
    // and get the count and label.
    const sectionsData = compact(
      entitySections.map((section) => {
        const sectionSuggestions = getAISuggestionsForEntitySection(
          entityType,
          section,
          suggestionsByKind
        ).filter((s) => !s.acceptanceStatus);

        const label = getLabelForSection(section, entityType);
        if (label === null) {
          return null;
        }

        return {
          section,
          count: sectionSuggestions.length,
          label,
        };
      })
    );

    return sectionsData.filter(({ count }) => count > 0);
  }, [defaultFirstToDieClientId, entity.kind, entitySuggestionsV2]);

  // Calculate total suggestions count
  const totalHoldingsSuggestions = holdingsSuggestions.length;
  const totalEntityDetailsSuggestions = entitySectionSuggestions.reduce(
    (sum, item) => sum + item.count,
    0
  );
  const totalCount = totalHoldingsSuggestions + totalEntityDetailsSuggestions;

  const suggestionOptions = [
    ...(totalHoldingsSuggestions > 0
      ? [
          {
            key: 'holdings',
            label: 'Holdings',
            count: totalHoldingsSuggestions,
            onClick: () => onHoldingsSuggestionClick?.(),
          },
        ]
      : []),
    ...entitySectionSuggestions.map(({ section, count, label }) => ({
      key: `${entity.id}-${section}`,
      label,
      count,
      onClick: () => onEditEntitySectionSuggestionClick?.(section),
    })),
  ];

  return (
    <EstateOnboardingSuggestionsButton
      dataTestId="estate-onboarding-entity-suggestions-button"
      totalCount={totalCount}
      size={size}
      variant={variant}
      popperVariant={popperVariant}
      isGenerating={isGenerating}
      suggestionOptions={suggestionOptions}
      sx={{
        ...sx,
        minWidth: '148px',
      }}
      {...props}
    />
  );
}
